import { IAppStore } from '@/models/store.model'
import { webApiService } from '@/services'
import { StatusColor } from '@/utils/StatusColor.utility'
import { currencyFormat } from '@/utils/currencyFormat'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Selection, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { FcMultipleInputs } from 'react-icons/fc'
import { useSelector } from 'react-redux'
import { Button } from '@/components/ui/button'
import { useTradeinContext } from '../hooks/useTradeinContext'
import { IConfigSeller } from '@/models/configSeller'
import moment from 'moment'
import { Document, Page, pdf } from '@react-pdf/renderer'
import Html from 'react-pdf-html'
import { blobToBase64 } from '@/utils/blobToBase64.util'
import { generateBarcodeBase64 } from '@/utils/makeBarCode.util'
import { getMacstoreBarcodeTexts } from '@/utils/macstoreBarcodes.util'
import { htmlReplaceContract } from '@/utils/htmlReplaceContract'

const gradeOrder = ['A', 'B', 'C', 'D']
const bestGrade = (grade?: string, gradeIa?: string): string => {
  if (!gradeIa) return String(grade ?? '')
  return gradeOrder.indexOf(gradeIa) > gradeOrder.indexOf(String(grade)) ? gradeIa : String(grade ?? '')
}

const MultipleReceipt = () => {
  const [data, setData] = useState<{ loading: boolean, openModal: boolean, listMulti: any[] }>({ loading: false, openModal: false, listMulti: [] })
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set<number>())
  const { ctx, saveCtx } = useTradeinContext()
  const userState = useSelector((store: IAppStore) => store.user)
  const isMacstoreMexico = configSeller.config.s3.FOLDER_APP === 'ti_macstore'

  const total = data.listMulti === undefined ? 0 : (selectedKeys === 'all') ? data.listMulti.reduce((valorAnterior, valorActual) => { return valorAnterior + valorActual.new_discount_amount }, 0) : data.listMulti.filter((item) => selectedKeys.has(String(item.id))).reduce((valorAnterior, valorActual) => { return valorAnterior + valorActual.new_discount_amount }, 0)

  const allSeries = data.listMulti === undefined ? '' : (selectedKeys === 'all') ? data.listMulti.map(item => item.serie).join('-') : data.listMulti.filter((item) => selectedKeys.has(String(item.id))).map(item => item.serie).join('-')
  console.log('🚀 ~ MultipleReceipt ~ allSeries:', allSeries)

  // Selección actual de equipos (objetos completos), ordenada por id_tradein
  const selectedDevices = (selectedKeys === 'all')
    ? [...data.listMulti]
    : data.listMulti.filter(item => selectedKeys.has(String(item.id)))
  const sortedDevices = [...selectedDevices].sort((a, b) => Number(a.id) - Number(b.id))

  // Genera el "contrato_multiple_final": el contrato original de cada equipo
  // (ordenado por id_tradein) y en cada uno TODOS los barcodes de todos los equipos.
  const buildMultipleFinalPdf = async (): Promise<string> => {
    // 1. Por equipo: obtengo storeCode + sku y armo sus textos de barcode
    const perDevice = await Promise.all(sortedDevices.map(async (item) => {
      const grade = bestGrade(item.grade, item.grade_IA)
      const sku = await webApiService.getSkuByModel({ grade, model: item.model, capacity: item.capacity })
        .then(res => res?.data)
        .catch(() => '')
      const storeCode = await webApiService.getStoreByUser(item.store_user ?? (ctx.tradein as any).store_user)
        .then(res => res.data[0].code)
        .catch(() => '')
      const texts = getMacstoreBarcodeTexts({
        campaign: item.campaign,
        storeCode,
        imei: item.serie,
        discountAmount: item.new_discount_amount ?? item.discount_amount,
        sku,
        id: item.id
      })
      return { item, texts }
    }))

    // 2. Bloque con TODOS los barcodes de todos los equipos (etiquetados por equipo, ordenados por id)
    // Orden en la cuadrícula 2x2:  barcode2 | barcode1
    //                             barcode3 | barcode4
    const allBarcodesHtml = perDevice.map(({ item, texts }) => {
      const cell = (t?: string): string => `<td style="width:50%;text-align:center;padding:4px">${t ? `<img style="width:100%;height:70px" src="${String(generateBarcodeBase64(t))}">` : ''}</td>`
      const grid =
        '<table style="width:100%">' +
        `<tr>${cell(texts[1])}${cell(texts[0])}</tr>` +
        `<tr>${cell(texts[2])}${cell(texts[3])}</tr>` +
        (texts[4] ? `<tr>${cell(texts[4])}<td style="width:50%"></td></tr>` : '') +
        '</table>'
      return `<div style="width:100%;margin-bottom:16px"><p style="font-weight:bold;font-size:11px">Equipo #${String(item.id)} - ${String(item.brand)} ${String(item.model)} ${String(item.capacity)}</p>${grid}</div>`
    }).join('')

    // 3. El cuerpo del contrato UNA sola vez + el bloque completo de barcodes
    let contractHtml = htmlReplaceContract(configSeller.config.html, {
      s3folder: configSeller.config.s3.FOLDER_APP,
      brand: ctx.tradein.brand,
      model: ctx.tradein.model,
      capacity: ctx.tradein.capacity,
      imei: ctx.tradein.serie,
      name: ctx.tradein.name,
      lastname: ctx.tradein.lastname
    })
    // Firma que el cliente ya dejó al firmar el contrato (misma ruta S3 que usa Header.updateContract)
    const contractName = String(ctx.tradein.url_contract ?? '').split('/').pop() || ''
    const urlFirma = `https://${import.meta.env.VITE_BUCKET}.s3.us-west-2.amazonaws.com/${configSeller.config.s3.FOLDER_APP}/contratos/${String(ctx.tradein.id)}/${contractName.replace('contrato.pdf', 'Signature.png')}`
    contractHtml = contractHtml.replace('{{firma}}', `<img style="height:80px" src="${urlFirma}">`)
    contractHtml = contractHtml.replace('{{qrCode}}', '')

    // Reemplazo la tabla de barcodes del template (celdas de media hoja con float:right)
    // por el bloque completo a ancho total. Conservo la fecha/hora que mostraba esa tabla.
    const fechaHora = moment().tz('America/Mexico_City').locale('es').format('DD/MM/YYYY hh:mm')
    const barcodesSection =
      `<table style="width:100%;border-collapse:collapse;border:none"><tbody><tr><td style="border:none">&nbsp;</td><td style="border:none;text-align:right">${fechaHora}</td></tr></tbody></table>` +
      allBarcodesHtml
    contractHtml = contractHtml.replace(/<table style="width: 100%; border-collapse: collapse; border: none;">[\s\S]*?<\/table>/, () => barcodesSection)
    contractHtml = contractHtml.replaceAll(/Times New Roman/gi, 'Helvetica')

    const pdfBlob = await pdf(
      <Document>
        <Page size='A4' style={{ fontFamily: 'Helvetica', fontWeight: 'bold' }}>
          <Html style={{ margin: '40px' }} resetStyles={false}>{contractHtml}</Html>
        </Page>
      </Document>
    ).toBlob()

    const base64 = (await blobToBase64(pdfBlob)) ?? ''
    // Para previsualizar: copia este data URL y pégalo en la barra del navegador
    console.log('🚀 ~ contrato_multiple_final base64 ==>', base64)

    return base64
  }

  const receiptDevices = async () => {
    setData({ ...data, loading: true })

    let pdfMultipleFinal = ''
    if (isMacstoreMexico) {
      try {
        pdfMultipleFinal = await buildMultipleFinalPdf()
      } catch (error: any) {
        console.log('🚀 ~ error generando contrato_multiple_final:', error)
        enqueueSnackbar('Error generando el contrato múltiple con códigos de barra', { variant: 'error' })
      }
    }

    webApiService.receiptDevices({ idList: (selectedKeys === 'all') ? data.listMulti.map(item => item.id) : Array.from(selectedKeys).map(item => Number(item)), amount: total, id_user: userState.id, series: allSeries, id_role: userState.id_role, email: userState.email, client_email: ctx.tradein.email, id_seller: ctx.tradein.id_seller, client_name: ctx.tradein.name, pdf: pdfMultipleFinal })
      .then((res) => {
        console.log('🚀 ~ .then ~ res:', res)
        enqueueSnackbar('Estado actualizado a Entregado', { variant: 'success' })
        setData({ ...data, loading: false })
        console.log('data receiptDevices ===>', data)
        saveCtx({ ...ctx, reload: true })
      })
      .catch((error: any) => {
        if (error.status === 500 && error.response.data !== undefined) {
          enqueueSnackbar(error.response.data.message, { variant: 'error' })
          enqueueSnackbar('Recargando pagina', { variant: 'warning' })
          saveCtx({ ...ctx, reload: true })
        } else {
          enqueueSnackbar(error.message, { variant: 'error' })
        }

        setData({ ...data, loading: false })
        console.log('error ==>', error)
      })
  }

  const handleClose = () => {
    setData({ ...data, openModal: false })
    setSelectedKeys(new Set())
  }

  const loadData = () => {
    setData({ ...data, loading: true })
    webApiService.getListMulti({ email: ctx.tradein.email, rut: ctx.tradein.rut, created_at: ctx.tradein.created_at, idSeller: ctx.tradein.id_seller }).then((res: any) => {
      if (res.data === undefined) {
        alert(1)
      }

      setData({ ...data, loading: false, listMulti: res.data ?? [] })
    }).catch((error) => {
      enqueueSnackbar(error.message, { variant: 'error' })
      console.log('error ==>', error)
      setData({ ...data, loading: false })
    })
  }

  const openModal = () => {
    setSelectedKeys(new Set([String(ctx.tradein.id)]))
    setData({ ...data, openModal: true })
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  console.log('setSelectedKeys', selectedKeys)
  return (
    <div hidden={data.listMulti === undefined || data.listMulti.length < 1}>
      <Button
        variant='outline'
        size='sm'
        legacyColor='default'
        legacyVariant='shadow'
        onClick={() => { openModal() }}
        disabled={data.loading}
      >
        <FcMultipleInputs />
        Recepción multiple
      </Button>
      <Modal backdrop='blur' isOpen={data.openModal} onClose={handleClose} size='xl'>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>Recepción múltiple</ModalHeader>
          <ModalBody>
            <Table
              aria-label='Example table with client side sorting'
              bottomContentPlacement='outside'
              selectedKeys={selectedKeys}
              selectionMode='multiple'
              onSelectionChange={setSelectedKeys}
              color='secondary'
            >
              <TableHeader>
                <TableColumn>#</TableColumn>
                <TableColumn>Estado</TableColumn>
                <TableColumn>Dispositivo</TableColumn>
                <TableColumn>Monto</TableColumn>
              </TableHeader>
              <TableBody
                isLoading={data.loading}
                loadingContent={<Spinner label='Loading...' />}
              >
                {
                        data.listMulti.map((item: any) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.id}</TableCell>
                            <TableCell><div className='rounded-full inline text-sm text-white px-2 py-1' style={{ backgroundColor: StatusColor(item.status) }}>{ctx.tradein.status}</div></TableCell>
                            <TableCell>{item.brand + ' ' + item.model + ' ' + item.capacity}</TableCell>
                            <TableCell>{currencyFormat(item.new_discount_amount, 'MX')}</TableCell>
                          </TableRow>
                        ))
                      }
              </TableBody>
            </Table>
            {
              !isMacstoreMexico && (
                <div className='flex justify-end'>
                  <p className='pe-5'>Total: <strong>{currencyFormat(total, 'MX')}</strong></p>
                </div>
              )
            }
          </ModalBody>
          <ModalFooter>
            <Button
              variant='destructive'
              onClick={handleClose}
            >
              Cerrar
            </Button>
            <Button type='submit' onClick={receiptDevices}>
              Recibir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default MultipleReceipt
